import type { BpsLogPoint, ChartPoint } from "./types";

type Key = "l1" | "l2" | "l3" | "l4" | "actual";

const DEFAULT_KEYS: Key[] = ["l1", "l2", "l3", "l4", "actual"];

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

const isGapPoint = (p: ChartPoint): boolean => p.label === "";

const isNullishForKey = (p: ChartPoint, k: Key): boolean => {
  const v = (p as any)?.[k];
  return !isFiniteNumber(v);
};

const addNullRunBoundariesToRequired = (
  points: ChartPoint[],
  segment: number[],
  keys: Key[],
  required: Set<number>,
) => {
  if (segment.length === 0) return;

  for (const k of keys) {
    let inNullRun = false;
    let runStartIdx: number | null = null;
    let prevIdx: number | null = null;

    for (let si = 0; si < segment.length; si++) {
      const idx = segment[si];
      const isNull = isNullishForKey(points[idx], k);

      if (isNull) {
        if (!inNullRun) {
          inNullRun = true;
          runStartIdx = idx;
        }
      } else {
        if (inNullRun) {
          // null 連続区間の境界点を残して、間引き後も線が誤って繋がらないようにする
          if (runStartIdx !== null) required.add(runStartIdx);
          if (prevIdx !== null) required.add(prevIdx);
          inNullRun = false;
          runStartIdx = null;
        }
      }

      prevIdx = idx;
    }

    if (inNullRun) {
      // 末尾が null のまま終わるケース
      if (runStartIdx !== null) required.add(runStartIdx);
      const lastIdx = segment[segment.length - 1];
      required.add(lastIdx);
    }
  }
};

const compressConsecutiveGaps = (points: ChartPoint[]) => {
  const out: ChartPoint[] = [];
  let prevWasGap = false;

  for (const p of points) {
    const gap = isGapPoint(p);
    if (gap) {
      if (!prevWasGap) out.push(p);
      prevWasGap = true;
      continue;
    }

    out.push(p);
    prevWasGap = false;
  }

  return out;
};

const pickEvenlySpacedIndices = (sorted: number[], take: number): number[] => {
  const n = sorted.length;
  if (take <= 0 || n === 0) return [];
  if (take >= n) return sorted.slice();

  const picked: number[] = [];
  const step = n / take;
  for (let i = 0; i < take; i++) {
    let idx = Math.floor(i * step);
    if (idx < 0) idx = 0;
    if (idx >= n) idx = n - 1;
    picked.push(sorted[idx]);
  }

  const uniq = Array.from(new Set(picked));
  uniq.sort((a, b) => a - b);

  if (uniq.length >= take) return uniq.slice(0, take);

  // uniqueで足りない場合は前から補完
  const set = new Set<number>(uniq);
  for (const v of sorted) {
    if (set.size >= take) break;
    set.add(v);
  }

  const filled = Array.from(set);
  filled.sort((a, b) => a - b);
  return filled.length > take ? filled.slice(0, take) : filled;
};

const allocateBudgets = (weights: number[], total: number): number[] => {
  if (total <= 0) return weights.map(() => 0);
  const sum = weights.reduce((a, b) => a + Math.max(0, b), 0);
  if (sum <= 0) return weights.map(() => 0);

  const raw = weights.map((w) => (total * Math.max(0, w)) / sum);
  const base = raw.map((v) => Math.floor(v));
  let remain = total - base.reduce((a, b) => a + b, 0);

  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);

  for (let k = 0; k < order.length && remain > 0; k++) {
    base[order[k].i] += 1;
    remain -= 1;
  }

  return base;
};

const selectSegmentIndices = (
  points: ChartPoint[],
  segment: number[],
  desiredCount: number,
  keys: Key[],
): number[] => {
  const len = segment.length;
  if (desiredCount <= 0 || len === 0) return [];
  if (desiredCount >= len) return segment.slice();

  const first = segment[0];
  const last = segment[len - 1];

  const maxPerBucket = 2 * keys.length + 2; // 先頭/末尾 + 各キーの最小/最大
  const bucketCount = Math.max(1, Math.floor(desiredCount / maxPerBucket));

  const chosen = new Set<number>();
  chosen.add(first);
  chosen.add(last);

  for (let b = 0; b < bucketCount; b++) {
    const start = Math.floor((b * len) / bucketCount);
    const end = Math.min(
      len - 1,
      Math.floor(((b + 1) * len) / bucketCount) - 1,
    );
    if (start > end) continue;

    const bucketFirst = segment[start];
    const bucketLast = segment[end];
    chosen.add(bucketFirst);
    chosen.add(bucketLast);

    for (const k of keys) {
      let minIdx: number | null = null;
      let maxIdx: number | null = null;
      let minV: number | null = null;
      let maxV: number | null = null;

      for (let i = start; i <= end; i++) {
        const idx = segment[i];
        const v = (points[idx] as any)[k];
        if (!isFiniteNumber(v)) continue;

        if (minV === null || v < minV) {
          minV = v;
          minIdx = idx;
        }
        if (maxV === null || v > maxV) {
          maxV = v;
          maxIdx = idx;
        }
      }

      if (minIdx !== null) chosen.add(minIdx);
      if (maxIdx !== null) chosen.add(maxIdx);
    }
  }

  const chosenSorted = Array.from(chosen).sort((a, b) => a - b);
  if (chosenSorted.length === desiredCount) return chosenSorted;

  if (chosenSorted.length > desiredCount) {
    // 先頭/末尾は維持しつつ、残りを均等に削る
    const required = new Set<number>([first, last]);
    const optional = chosenSorted.filter((i) => !required.has(i));
    const takeOptional = Math.max(0, desiredCount - required.size);
    const pickedOptional = pickEvenlySpacedIndices(optional, takeOptional);
    const all = Array.from(new Set<number>([...required, ...pickedOptional]));
    all.sort((a, b) => a - b);
    return all;
  }

  // 足りない場合は残りから均等に追加
  const remaining = segment.filter((i) => !chosen.has(i));
  const need = desiredCount - chosenSorted.length;
  const add = pickEvenlySpacedIndices(remaining, need);
  const all = Array.from(new Set<number>([...chosenSorted, ...add]));
  all.sort((a, b) => a - b);
  return all;
};

export const downsampleChartPoints = (
  input: ChartPoint[],
  maxPoints: number,
  keys: Key[] = DEFAULT_KEYS,
): ChartPoint[] => {
  // チャート点を情報を落としすぎない形で間引き、描画負荷を抑える。
  const safeMax =
    typeof maxPoints === "number" && Number.isFinite(maxPoints)
      ? Math.max(1, Math.floor(maxPoints))
      : 2000;

  const points = compressConsecutiveGaps(input);
  if (points.length <= safeMax) return points;

  const gapIdxs: number[] = [];
  const segments: number[][] = [];
  let cur: number[] = [];

  for (let i = 0; i < points.length; i++) {
    if (isGapPoint(points[i])) {
      gapIdxs.push(i);
      if (cur.length) {
        segments.push(cur);
        cur = [];
      }
      continue;
    }
    cur.push(i);
  }
  if (cur.length) segments.push(cur);

  const required = new Set<number>();

  // gap点は線の断絶表現に重要なので基本は保持
  for (const i of gapIdxs) required.add(i);

  // 各セグメントの先頭/末尾は必ず保持
  for (const s of segments) {
    required.add(s[0]);
    required.add(s[s.length - 1]);
  }

  // 間引きで欠損(null)の区間が消えると線が誤って繋がるので、null連続の境界点は required 扱いで保持する
  for (const s of segments) {
    addNullRunBoundariesToRequired(points, s, keys, required);
  }

  const requiredSortedWithNull = Array.from(required).sort((a, b) => a - b);
  if (requiredSortedWithNull.length >= safeMax) {
    const picked = pickEvenlySpacedIndices(requiredSortedWithNull, safeMax);
    return picked.map((i) => points[i]);
  }

  const remainBudget = safeMax - requiredSortedWithNull.length;
  const segWeights = segments.map((s) => s.length);
  const segBudgets = allocateBudgets(segWeights, remainBudget);

  const chosen = new Set<number>(requiredSortedWithNull);

  for (let si = 0; si < segments.length; si++) {
    const seg = segments[si];
    const desired = Math.min(seg.length, 2 + segBudgets[si]); // +2 は先頭/末尾
    const picked = selectSegmentIndices(points, seg, desired, keys);
    for (const idx of picked) chosen.add(idx);
  }

  let chosenSorted = Array.from(chosen).sort((a, b) => a - b);
  if (chosenSorted.length > safeMax) {
    const optional = chosenSorted.filter((i) => !required.has(i));
    const takeOptional = Math.max(0, safeMax - requiredSortedWithNull.length);
    const pickedOptional = pickEvenlySpacedIndices(optional, takeOptional);
    const all = Array.from(
      new Set<number>([...requiredSortedWithNull, ...pickedOptional]),
    );
    all.sort((a, b) => a - b);
    chosenSorted = all;
  }

  return chosenSorted.map((i) => points[i]);
};

const hasFiniteGps = (pt: BpsLogPoint) => {
  const lat = pt?.gps?.lat;
  const lng = pt?.gps?.lng;
  return isFiniteNumber(lat) && isFiniteNumber(lng);
};

const parseTimestampMs = (v: unknown): number | null => {
  if (typeof v !== "string") return null;
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : null;
};

const parseTimestampSecOffset = (
  tMs: number,
  startMs: number,
): number | null => {
  if (!Number.isFinite(tMs) || !Number.isFinite(startMs)) return null;
  const dMs = tMs - startMs;
  if (!Number.isFinite(dMs)) return null;
  return Math.floor(dMs / 1000);
};

// API側のUI間引き（BpsLogController::getRepresentativeModemIdsForRange）と同様に
// ギャップ境界(≧gapThresholdMs)を必ず含めつつ、時間バケットで等間隔に代表点を選ぶ。
// NOTE: 期間指定レンジ取得（created_at昇順）を前提とする。
export const pickRepresentativeBpsLogIndicesForRangeLikeApiCap = (args: {
  input: BpsLogPoint[];
  take: number;
  gapThresholdMs?: number;
  rangeStartMs?: number | null;
  rangeEndMs?: number | null;
}): number[] => {
  const {
    input,
    take,
    gapThresholdMs = 3_000,
    rangeStartMs,
    rangeEndMs,
  } = args;

  const safeTake =
    typeof take === "number" && Number.isFinite(take)
      ? Math.max(1, Math.floor(take))
      : 5000;

  if (!Array.isArray(input) || input.length === 0) return [];
  if (input.length <= safeTake) {
    return Array.from({ length: input.length }, (_, i) => i);
  }

  // 1) ギャップでセグメント分割し、境界（開始/終了）を required として確保
  const segments: number[][] = [];
  let cur: number[] = [];

  const safeGap = Math.max(0, gapThresholdMs);
  let prevMs: number | null = null;

  for (let i = 0; i < input.length; i++) {
    const ms = parseTimestampMs(input[i]?.timestamp);
    if (ms === null) {
      if (cur.length) {
        segments.push(cur);
        cur = [];
      }
      prevMs = null;
      continue;
    }

    if (prevMs !== null && ms - prevMs >= safeGap) {
      if (cur.length) {
        segments.push(cur);
        cur = [];
      }
    }

    cur.push(i);
    prevMs = ms;
  }
  if (cur.length) segments.push(cur);

  const requiredEdges = new Set<number>();
  for (const s of segments) {
    requiredEdges.add(s[0]);
    requiredEdges.add(s[s.length - 1]);
  }
  requiredEdges.add(0);
  requiredEdges.add(input.length - 1);

  const requiredEdgeSorted = Array.from(requiredEdges).sort((a, b) => a - b);
  if (requiredEdgeSorted.length > safeTake) {
    // API側は too_many_gap_boundaries(413) になるケース。
    // フロントは描画継続を優先して均等間引きで逃がす。
    const picked = pickEvenlySpacedIndices(requiredEdgeSorted, safeTake);
    return picked;
  }

  const required = requiredEdgeSorted;
  if (required.length >= safeTake) return required;

  // 2) 時間バケットで等間隔サンプリング（各バケットの最小modem_id相当＝最小の(時刻,ID)）
  const fallbackStartMs = parseTimestampMs(input[0]?.timestamp);
  const fallbackEndMs = parseTimestampMs(input[input.length - 1]?.timestamp);

  const startMsRaw =
    typeof rangeStartMs === "number" && Number.isFinite(rangeStartMs)
      ? rangeStartMs
      : fallbackStartMs;
  const endMsRaw =
    typeof rangeEndMs === "number" && Number.isFinite(rangeEndMs)
      ? rangeEndMs
      : fallbackEndMs;

  const startMs =
    typeof startMsRaw === "number" && Number.isFinite(startMsRaw)
      ? startMsRaw
      : 0;
  const endMs =
    typeof endMsRaw === "number" && Number.isFinite(endMsRaw)
      ? endMsRaw
      : startMs;

  const rangeSec = Math.max(0, Math.floor((endMs - startMs) / 1000));
  const bucketSec = Math.max(
    1,
    Math.ceil(rangeSec / Math.max(1, safeTake - 1)),
  );

  const bucketToBest = new Map<
    number,
    { idx: number; tMs: number; modemId: number }
  >();

  for (let i = 0; i < input.length; i++) {
    const tMs = parseTimestampMs(input[i]?.timestamp);
    if (tMs === null) continue;
    const secOffset = parseTimestampSecOffset(tMs, startMs);
    if (secOffset === null) continue;
    if (secOffset < 0) continue;
    const b = Math.floor(secOffset / bucketSec);

    const modemIdRaw = (input[i] as any)?.modem_id;
    const modemId =
      typeof modemIdRaw === "number" && Number.isFinite(modemIdRaw)
        ? modemIdRaw
        : Number.MAX_SAFE_INTEGER;

    const prev = bucketToBest.get(b);
    if (!prev) {
      bucketToBest.set(b, { idx: i, tMs, modemId });
      continue;
    }

    // (created_at, modem_id) の昇順で最小を選ぶ
    if (tMs < prev.tMs || (tMs === prev.tMs && modemId < prev.modemId)) {
      bucketToBest.set(b, { idx: i, tMs, modemId });
    }
  }

  const sampledSorted = Array.from(bucketToBest.values())
    .map((v) => v.idx)
    .sort((a, b) => a - b);

  const pickedSet = new Set<number>(required);
  const final = required.slice();
  for (const idx of sampledSorted) {
    if (final.length >= safeTake) break;
    if (pickedSet.has(idx)) continue;
    pickedSet.add(idx);
    final.push(idx);
  }
  final.sort((a, b) => a - b);
  return final;
};

const selectBpsSegmentIndices = (
  points: BpsLogPoint[],
  segment: number[],
  desiredCount: number,
): number[] => {
  const len = segment.length;
  if (desiredCount <= 0 || len === 0) return [];
  if (desiredCount >= len) return segment.slice();

  const first = segment[0];
  const last = segment[len - 1];

  const maxPerBucket = 6; // 先頭/末尾 + (バケツ先頭/末尾) + (actualの最小/最大)
  const bucketCount = Math.max(1, Math.floor(desiredCount / maxPerBucket));

  const chosen = new Set<number>();
  chosen.add(first);
  chosen.add(last);

  for (let b = 0; b < bucketCount; b++) {
    const start = Math.floor((b * len) / bucketCount);
    const end = Math.min(
      len - 1,
      Math.floor(((b + 1) * len) / bucketCount) - 1,
    );
    if (start > end) continue;

    const bucketFirst = segment[start];
    const bucketLast = segment[end];
    chosen.add(bucketFirst);
    chosen.add(bucketLast);

    let minIdx: number | null = null;
    let maxIdx: number | null = null;
    let minV: number | null = null;
    let maxV: number | null = null;

    for (let i = start; i <= end; i++) {
      const idx = segment[i];
      const v = points[idx]?.actual_bitrate;
      if (!isFiniteNumber(v)) continue;

      if (minV === null || v < minV) {
        minV = v;
        minIdx = idx;
      }
      if (maxV === null || v > maxV) {
        maxV = v;
        maxIdx = idx;
      }
    }

    if (minIdx !== null) chosen.add(minIdx);
    if (maxIdx !== null) chosen.add(maxIdx);
  }

  const chosenSorted = Array.from(chosen).sort((a, b) => a - b);
  if (chosenSorted.length === desiredCount) return chosenSorted;

  if (chosenSorted.length > desiredCount) {
    const required = new Set<number>([first, last]);
    const optional = chosenSorted.filter((i) => !required.has(i));
    const takeOptional = Math.max(0, desiredCount - required.size);
    const pickedOptional = pickEvenlySpacedIndices(optional, takeOptional);
    const all = Array.from(new Set<number>([...required, ...pickedOptional]));
    all.sort((a, b) => a - b);
    return all;
  }

  const remaining = segment.filter((i) => !chosen.has(i));
  const need = desiredCount - chosenSorted.length;
  const add = pickEvenlySpacedIndices(remaining, need);
  const all = Array.from(new Set<number>([...chosenSorted, ...add]));
  all.sort((a, b) => a - b);
  return all;
};

export const downsampleBpsLogPointsForMap = (
  input: BpsLogPoint[],
  maxPoints: number,
  gapThresholdMs: number = 3_000,
): BpsLogPoint[] => {
  // マップの軌跡用にGPS点だけを抽出して間引き、点数上限内に収める。
  const safeMax =
    typeof maxPoints === "number" && Number.isFinite(maxPoints)
      ? Math.max(1, Math.floor(maxPoints))
      : 2000;

  if (!Array.isArray(input) || input.length === 0) return [];

  // マップの軌跡はGPSがある点のみ使う
  const gpsPoints = input.filter((pt) => pt && hasFiniteGps(pt));
  if (gpsPoints.length <= safeMax) return gpsPoints;

  const segments: number[][] = [];
  let cur: number[] = [];

  for (let i = 0; i < gpsPoints.length; i++) {
    const ms = parseTimestampMs(gpsPoints[i]?.timestamp);
    if (ms === null) {
      if (cur.length) {
        segments.push(cur);
        cur = [];
      }
      continue;
    }

    if (cur.length) {
      const prevIdx = cur[cur.length - 1];
      const prevMs = parseTimestampMs(gpsPoints[prevIdx]?.timestamp);
      if (prevMs !== null && ms - prevMs >= Math.max(0, gapThresholdMs)) {
        // 途切れ扱い: セグメントを分ける（境界の点は各セグメントの端として保持される）
        segments.push(cur);
        cur = [];
      }
    }

    cur.push(i);
  }
  if (cur.length) segments.push(cur);

  const required = new Set<number>();
  for (const s of segments) {
    required.add(s[0]);
    required.add(s[s.length - 1]);
  }

  const requiredSorted = Array.from(required).sort((a, b) => a - b);
  if (requiredSorted.length >= safeMax) {
    const picked = pickEvenlySpacedIndices(requiredSorted, safeMax);
    return picked.map((i) => gpsPoints[i]);
  }

  const remainBudget = safeMax - requiredSorted.length;
  const segWeights = segments.map((s) => s.length);
  const segBudgets = allocateBudgets(segWeights, remainBudget);

  const chosen = new Set<number>(requiredSorted);
  for (let si = 0; si < segments.length; si++) {
    const seg = segments[si];
    const desired = Math.min(seg.length, 2 + segBudgets[si]);
    const picked = selectBpsSegmentIndices(gpsPoints, seg, desired);
    for (const idx of picked) chosen.add(idx);
  }

  let chosenSorted = Array.from(chosen).sort((a, b) => a - b);
  if (chosenSorted.length > safeMax) {
    const optional = chosenSorted.filter((i) => !required.has(i));
    const takeOptional = Math.max(0, safeMax - requiredSorted.length);
    const pickedOptional = pickEvenlySpacedIndices(optional, takeOptional);
    const all = Array.from(
      new Set<number>([...requiredSorted, ...pickedOptional]),
    );
    all.sort((a, b) => a - b);
    chosenSorted = all;
  }

  return chosenSorted.map((i) => gpsPoints[i]);
};
